import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import type { GoogleContact } from "@/services/contactsService";
import { contactsService } from "@/services/contactsService";
import { Building2, User, X } from "lucide-react";
import type { KeyboardEvent } from "react";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";

interface MultiEmailAutocompleteProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxEmails?: number;
  helperText?: string;
}

export interface MultiEmailAutocompleteRef {
  commitPendingEmail: () => void;
}

/**
 * Get initials from name or email
 */
const getInitials = (name?: string, email?: string): string => {
  if (name) {
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return "U";
};

/**
 * Get source icon for contact
 */
const SourceIcon: React.FC<{ source: GoogleContact["source"] }> = ({
  source,
}) => {
  switch (source) {
    case "directory":
      return <Building2 className="h-3 w-3 text-blue-500" />;
    default:
      return <User className="h-3 w-3 text-gray-500" />;
  }
};

const parseEmails = (str: string): string[] => {
  return str
    .split(/[\s,]+/)
    .map((e) => e.trim())
    .filter((e) => e.length > 0);
};

export const MultiEmailAutocomplete = forwardRef<
  MultiEmailAutocompleteRef,
  MultiEmailAutocompleteProps
>(
  (
    {
      value = [],
      onChange,
      placeholder = "Type to search contacts...",
      disabled = false,
      className = "",
      maxEmails,
      helperText,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState("");
    const [open, setOpen] = useState(false);
    const [contacts, setContacts] = useState<GoogleContact[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    // Normalize value to array
    const emails: string[] = Array.isArray(value)
      ? value.flatMap((v) => parseEmails(v))
      : parseEmails(value as unknown as string);

    // Store latest emails in ref to avoid dependency issues
    const emailsRef = useRef(emails);
    useEffect(() => {
      emailsRef.current = emails;
    }, [emails]);

    // Search contacts when input changes
    const searchContacts = useCallback(async (query: string) => {
      if (!query || query.length < 2) {
        setContacts([]);
        return;
      }

      setIsLoading(true);
      try {
        const results = await contactsService.searchContacts(query);
        // Filter out already selected emails using ref
        const filtered = results.filter(
          (c) =>
            !emailsRef.current.some(
              (e) => e.toLowerCase() === c.email.toLowerCase(),
            ),
        );
        setContacts(filtered);
        setSelectedIndex(-1);
      } catch (error: any) {
        console.error("Error searching contacts:", error);
        // Don't show error for rate limiting - just return empty results
        if (error?.status !== 429) {
          console.error("Error searching contacts:", error);
        }
        setContacts([]);
      } finally {
        setIsLoading(false);
      }
    }, []); // No dependencies - use ref for emails

    // Debounced search with longer delay to reduce API calls
    useEffect(() => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Increase debounce delay to 500ms to reduce API calls
      debounceRef.current = setTimeout(() => {
        searchContacts(inputValue);
      }, 500);

      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, [inputValue, searchContacts]);

    // Check if at max capacity
    const isAtMaxCapacity =
      maxEmails !== undefined && emails.length >= maxEmails;

    // Add email to the list
    const addEmail = (email: string) => {
      const trimmed = email.trim();
      if (maxEmails !== undefined && emails.length >= maxEmails) {
        setInputValue("");
        setContacts([]);
        setOpen(false);
        return;
      }
      if (trimmed && !emails.includes(trimmed)) {
        const newEmails = [...emails, trimmed];
        onChange(newEmails);
      }
      setInputValue("");
      setContacts([]);
      setOpen(false);
      setSelectedIndex(-1);
      inputRef.current?.focus();
    };

    const addMultipleEmails = (emailString: string) => {
      const parsedEmails = parseEmails(emailString);
      const validEmails = parsedEmails.filter((e) => e.includes("@"));

      if (validEmails.length === 0) return;

      if (
        maxEmails !== undefined &&
        emails.length + validEmails.length > maxEmails
      ) {
        const remaining = maxEmails - emails.length;
        validEmails.splice(remaining);
      }

      const newEmails = [...emails];
      for (const email of validEmails) {
        if (!newEmails.includes(email)) {
          newEmails.push(email);
        }
      }

      if (newEmails.length !== emails.length) {
        onChange(newEmails);
      }

      setInputValue("");
      setContacts([]);
      setOpen(false);
      setSelectedIndex(-1);
      inputRef.current?.focus();
    };

    // Remove email from the list
    const removeEmail = (email: string) => {
      const newEmails = emails.filter((e) => e !== email);
      onChange(newEmails);
    };

    // Handle contact selection
    const handleSelect = (contact: GoogleContact) => {
      addEmail(contact.email);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (newValue.includes(" ") || newValue.includes(",")) {
        const parts = parseEmails(newValue);
        const validParts = parts.filter((p) => p.includes("@"));

        if (validParts.length > 0) {
          addMultipleEmails(newValue);
          return;
        }
      }

      setInputValue(newValue);
      if (newValue.length >= 2) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    };

    const handleBlur = () => {
      if (inputValue.trim() && inputValue.includes("@")) {
        if (inputValue.includes(" ") || inputValue.includes(",")) {
          addMultipleEmails(inputValue);
        } else {
          addEmail(inputValue);
        }
      }
    };

    useImperativeHandle(ref, () => ({
      commitPendingEmail: () => {
        if (inputValue.trim() && inputValue.includes("@")) {
          if (inputValue.includes(" ") || inputValue.includes(",")) {
            addMultipleEmails(inputValue);
          } else {
            addEmail(inputValue);
          }
        }
      },
    }));

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        setOpen(false);
        setSelectedIndex(-1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (contacts.length > 0) {
          setOpen(true);
          setSelectedIndex((prev) => Math.min(prev + 1, contacts.length - 1));
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedIndex >= 0 && contacts[selectedIndex]) {
          handleSelect(contacts[selectedIndex]);
        } else if (inputValue.includes("@")) {
          if (inputValue.includes(" ") || inputValue.includes(",")) {
            addMultipleEmails(inputValue);
          } else {
            addEmail(inputValue);
          }
        }
      } else if (e.key === "," || e.key === "Tab") {
        if (inputValue.includes("@")) {
          e.preventDefault();
          if (inputValue.includes(" ") || inputValue.includes(",")) {
            addMultipleEmails(inputValue);
          } else {
            addEmail(inputValue);
          }
        }
      } else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
        // Remove last email when backspace is pressed on empty input
        removeEmail(emails[emails.length - 1]);
      }
    };

    // Close popover when clicking outside
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target as Node)
        ) {
          setOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const showPopover = open && contacts.length > 0;

    return (
      <div className={`relative ${className}`} ref={containerRef}>
        <div
          className="flex flex-wrap gap-1 p-2 min-h-[42px] border rounded-md bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
          onClick={() => inputRef.current?.focus()}
        >
          {/* Email badges */}
          {emails.map((email, index) => (
            <Badge
              key={`${email}-${index}`}
              variant="secondary"
              className="gap-1 pr-1 text-xs"
            >
              {email}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEmail(email);
                  }}
                  className="ml-1 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}

          {/* Input field */}
          {!isAtMaxCapacity && (
            <input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={() => {
                if (inputValue.length >= 2 && contacts.length > 0) {
                  setOpen(true);
                }
              }}
              placeholder={emails.length === 0 ? placeholder : ""}
              disabled={disabled}
              className="flex-1 min-w-[150px] bg-transparent border-none outline-none text-sm"
              autoComplete="off"
            />
          )}
        </div>

        {/* Suggestions dropdown */}
        {showPopover && (
          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg overflow-hidden">
            {isLoading ? (
              <div className="p-3 text-sm text-muted-foreground text-center">
                Searching contacts...
              </div>
            ) : (
              <ul className="max-h-[200px] overflow-y-auto">
                {contacts.map((contact, index) => (
                  <li
                    key={`${contact.email}-${index}`}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                      index === selectedIndex
                        ? "bg-accent"
                        : "hover:bg-accent/50"
                    }`}
                    onClick={() => handleSelect(contact)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarImage
                        src={contact.photo}
                        alt={contact.name || contact.email}
                      />
                      <AvatarFallback className="text-xs">
                        {getInitials(contact.name, contact.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      {contact.name && (
                        <div className="text-sm font-medium truncate">
                          {contact.name}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground truncate">
                        {contact.email}
                      </div>
                    </div>
                    <SourceIcon source={contact.source} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Helper text */}
        {helperText && (
          <p className="text-xs text-muted-foreground mt-1">{helperText}</p>
        )}
        {!helperText && emails.length > 0 && !isAtMaxCapacity && (
          <p className="text-xs text-muted-foreground mt-1">
            Press Enter, Tab, or comma to add
          </p>
        )}
      </div>
    );
  },
);

MultiEmailAutocomplete.displayName = "MultiEmailAutocomplete";

export default MultiEmailAutocomplete;
